import { useEffect, useId, useRef, type CSSProperties, type ReactNode } from "react";

import { sx } from "../style";

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  width?: number | string;
  danger?: boolean;
  /** Hide the built-in header «×» (e.g. when the footer already has a close button). */
  hideClose?: boolean;
  style?: CSSProperties;
}

/** Modal dialog with overlay. Renders nothing when `open` is false. */
export function Modal({ open, onClose, title, children, footer, width = 520, danger = false, hideClose = false, style }: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose; // завжди свіжий, але НЕ у deps (інакше effect re-run щорендера)
  const titleId = useId();

  // TD-0058: Escape→close, focus-trap (Tab циклічний), фокус у діалог на відкритті,
  // відновлення фокуса на trigger при закритті — вимоги WAI-ARIA dialog. Залежність
  // лише від `open`: запуск раз на відкриття/закриття, без перезапуску щорендера.
  useEffect(() => {
    if (!open) return;
    restoreFocusRef.current = (document.activeElement as HTMLElement | null) ?? null;
    const dialog = dialogRef.current;
    const focusables = dialog?.querySelectorAll<HTMLElement>(FOCUSABLE);
    (focusables && focusables.length ? focusables[0] : dialog)?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (e.key !== "Tab" || !dialog) return;
      const items = dialog.querySelectorAll<HTMLElement>(FOCUSABLE);
      if (items.length === 0) {
        e.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      restoreFocusRef.current?.focus?.();
    };
  }, [open]);

  if (!open) return null;
  return (
    <div
      className="cf-modal__overlay"
      onClick={onClose}
      style={sx({
        position: "fixed",
        inset: 0,
        zIndex: "var(--z-modal)",
        background: "rgba(10,30,60,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        animation: "cf-fade-in var(--duration-fast) var(--ease-standard)",
      })}
    >
      <div
        className="cf-modal__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        ref={dialogRef}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        style={sx({
          width,
          maxWidth: "100%",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          background: "var(--neutral-0)",
          borderRadius: "var(--radius-lg)",
          boxShadow: "var(--shadow-modal)",
          animation: "cf-slide-up var(--duration-base) var(--ease-out)",
          overflow: "hidden",
          ...style,
        })}
      >
        <div className="cf-modal__header" style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, padding: "18px 20px 14px" }}>
          <h2
            id={titleId}
            style={sx({
              margin: 0,
              fontSize: "var(--fs-h3)",
              lineHeight: "var(--lh-h3)",
              fontWeight: "var(--fw-semibold)",
              color: danger ? "var(--color-status-error)" : "var(--color-text-primary)",
            })}
          >
            {title}
          </h2>
          {!hideClose && (
            <button
              className="cf-modal__close"
              type="button"
              onClick={onClose}
              aria-label="Закрити"
              style={{ border: "none", background: "transparent", color: "var(--color-text-tertiary)", cursor: "pointer", flex: "none", padding: 2 }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
              </svg>
            </button>
          )}
        </div>
        <div
          className="cf-modal__body"
          style={sx({
            padding: "0 20px 20px",
            overflowY: "auto",
            fontSize: "var(--fs-body)",
            color: "var(--color-text-secondary)",
            lineHeight: "var(--lh-body)",
          })}
        >
          {children}
        </div>
        {footer && (
          <div
            className="cf-modal__footer"
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 8,
              padding: "14px 20px",
              borderTop: "1px solid var(--color-border-subtle)",
              background: "var(--neutral-25)",
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
